import { useEffect, useState } from "react";
import EmailVerification from "supertokens-web-js/recipe/emailverification";
import MultiFactorAuthentication from "supertokens-web-js/recipe/multifactorauth";
import Session from "supertokens-web-js/recipe/session";

import { useAccessTokenContext } from "@/providers/AccessTokenProvider";

export enum AuthenticationState {
  Loading,
  Failed,
  NotLoggedIn,
  LoggedInButEmailNotVerified,
  LoggedInButMFANotCompleted,
  LoggedIn,
}

export function useAuthenticationState(): AuthenticationState {
  const localDevelopmentAutoLogin = import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_AUTO_LOGIN === "true";
  const [state, setState] = useState<AuthenticationState>(
    localDevelopmentAutoLogin ? AuthenticationState.LoggedIn : AuthenticationState.Loading,
  );

  useEffect(() => {
    let cancelled = false;

    if (localDevelopmentAutoLogin) {
      return;
    }

    if (state !== AuthenticationState.Loading) {
      return;
    }

    void Session.doesSessionExist()
      .then(async (sessionExists) => {
        if (cancelled) {
          return;
        }

        if (!sessionExists) {
          setState(AuthenticationState.NotLoggedIn);

          return;
        }

        const validationErrors = await Session.validateClaims();
        if (cancelled) {
          return;
        }

        if (validationErrors.length === 0) {
          setState(AuthenticationState.LoggedIn);

          return;
        }

        for (const err of validationErrors) {
          if (err.id === MultiFactorAuthentication.MultiFactorAuthClaim.id) {
            const mfaClaimValue = await Session.getClaimValue({
              claim: MultiFactorAuthentication.MultiFactorAuthClaim,
            });
            if (cancelled) {
              return;
            }

            if (mfaClaimValue === undefined || !("totp" in mfaClaimValue.c)) {
              setState(AuthenticationState.LoggedInButMFANotCompleted);

              return;
            }
          }

          if (err.id === EmailVerification.EmailVerificationClaim.id) {
            setState(AuthenticationState.LoggedInButEmailNotVerified);

            return;
          }
        }

        setState(AuthenticationState.Failed);
      })
      .catch(() => {
        if (!cancelled) {
          setState(AuthenticationState.Failed);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [localDevelopmentAutoLogin, state]);

  return state;
}

// These compatibility names also support raw quick-reply credentials.
export function useSuperTokensUserId(): string | undefined {
  return useAccessTokenContext().payload?.sub;
}

export function useSuperTokensUserEmail(): string | undefined {
  return useAccessTokenContext().payload?.emails?.[0];
}
